import { DonationPayload, GuestSessionResponse } from '../types';
import { API_KEY, CLIENT_APP, SOURCE_APP_SLUG } from './clientConfig';

const API_BASE = 'https://dev.oliveeb.com';
const IDENTITY_API = `${API_BASE}/identity-service/api/v1/auth`;
const OLITASK_API = `${API_BASE}/olitask-service/api/v1`;
const GATEWAY_API = `${API_BASE}`;

export async function getClientId(): Promise<string | null> {
  let clientId = localStorage.getItem('clientId');
  if (clientId && clientId !== 'undefined' && clientId !== 'null') {
    return clientId;
  }

  try {
    const response = await fetch(`${GATEWAY_API}/api/v1/clients/register/request`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'O-API-KEY': API_KEY,
        'O-CLIENT-APP': CLIENT_APP
      }
    });

    if (response.ok) {
      const data = await response.json();
      clientId = data.id;
      if (clientId) {
        localStorage.setItem('clientId', clientId);
        return clientId;
      }
    }
  } catch (error) {
    console.error('Failed to get clientId:', error);
  }
  return null;
}

export function getDeviceId(): string {
  let deviceId = localStorage.getItem('websiteDeviceId');
  if (!deviceId) {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      deviceId = 'web-' + crypto.randomUUID();
    } else {
      deviceId = 'web-' + Date.now() + '-' + Math.random().toString(36).substring(2, 15);
    }
    localStorage.setItem('websiteDeviceId', deviceId);
  }
  return deviceId;
}

export async function createGuestSession(): Promise<GuestSessionResponse | null> {
  try {
    const deviceId = getDeviceId();
    const response = await fetch(`${IDENTITY_API}/guest-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'O-CLIENT-APP': CLIENT_APP
      },
      body: JSON.stringify({
        deviceId: deviceId,
        platform: 'web',
        app: SOURCE_APP_SLUG,
        appVersion: '1.0.0'
      })
    });

    const responseText = await response.text();
    if (!response.ok) {
      throw new Error('Failed to create guest session: ' + responseText);
    }

    const data = JSON.parse(responseText);
    const token = data.accessToken;
    const sessionId = data.sessionId;
    const userId = data.userId;

    if (token && token !== 'undefined' && token !== 'null') {
      localStorage.setItem('guestToken', token);
      localStorage.setItem('guestSessionId', sessionId);
      localStorage.setItem('guestUserId', userId);
      return { accessToken: token, sessionId, userId };
    }
  } catch (error) {
    console.error('Guest session error:', error);
  }
  return null;
}

export async function ensureGuestSession(): Promise<string | null> {
  let guestToken = localStorage.getItem('guestToken');
  if (!guestToken || guestToken === 'undefined' || guestToken === 'null') {
    const session = await createGuestSession();
    if (session) {
      guestToken = session.accessToken;
    }
  }
  return guestToken;
}

export async function fetchStripeConfig(): Promise<string | null> {
  const token = await ensureGuestSession();
  const clientId = await getClientId();

  if (!clientId) return null;

  try {
    const response = await fetch(`${OLITASK_API}/app-config`, {
      headers: {
        'Content-Type': 'application/json',
        'O-CLIENT-APP': CLIENT_APP,
        'O-API-KEY': API_KEY,
        'O-CLIENT-ID': clientId,
        'Authorization': token ? `Bearer ${token}` : ''
      }
    });

    if (response.ok) {
      const data = await response.json();
      return data.stripePublishableKey || null;
    }
  } catch (error) {
    console.error('Failed to fetch Stripe config:', error);
  }
  return null;
}

export async function createDonation(payload: DonationPayload): Promise<any> {
  const token = await ensureGuestSession();
  const clientId = await getClientId();

  if (!token) {
    throw new Error('Unable to authenticate. Please refresh and try again.');
  }

  if (!clientId) {
    throw new Error('Unable to get client ID. Please refresh and try again.');
  }

  const response = await fetch(`${OLITASK_API}/guest/donations`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'O-CLIENT-APP': CLIENT_APP,
      'O-API-KEY': API_KEY,
      'O-CLIENT-ID': clientId
    },
    body: JSON.stringify(payload)
  });

  const responseBody = await response.text();
  if (!response.ok) {
    let message = 'Donation failed. Please try again.';
    try {
      const errorBody = responseBody ? JSON.parse(responseBody) : null;
      message = errorBody?.message || message;
    } catch {
      message = responseBody || message;
    }
    throw new Error(message);
  }

  return responseBody ? JSON.parse(responseBody) : {};
}
